import {useCallback, useEffect, useRef, useState} from 'react'
import {
    MdDelete,
    MdFingerprint,
    MdKey,
    MdSmartphone,
    MdComputer,
    MdTablet,
    MdCloud
} from 'react-icons/md'
import {FaApple, FaWindows, FaAndroid, FaChrome, FaFirefox, FaEdge, FaSafari} from 'react-icons/fa'

interface PasskeyCardProps {
    credential: {
        credentialId: string
        friendlyName?: string
        createdAt: number
        metadata?: {
            browser?: string
            operating_system?: string
            device_type?: string
            is_synced?: boolean
            sync_provider?: string
            last_used?: string
            platform?: string
            screen_resolution?: string
        }
    }
    onEdit: (credentialId: string, newName: string) => Promise<void>
    onDelete: (credentialId: string, name: string) => void
    isDeleting?: boolean
}

export function PasskeyCard({credential, onEdit, onDelete, isDeleting}: PasskeyCardProps) {
    // Local state for the editable name (optimistic UI)
    const [localName, setLocalName] = useState(credential.friendlyName || '')
    const [isSyncing, setIsSyncing] = useState(false)
    const [syncError, setSyncError] = useState<string | null>(null)

    // Refs for debouncing
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const previousNameRef = useRef(credential.friendlyName || '')

    // Update local name when credential changes from parent
    useEffect(() => {
        setLocalName(credential.friendlyName || '')
        previousNameRef.current = credential.friendlyName || ''
    }, [credential.friendlyName])

    // Debounced save function
    const debouncedSave = useCallback(async (credentialId: string, newName: string) => {
        if (newName.trim() === '' || newName === previousNameRef.current) {
            setIsSyncing(false)
            return
        }

        // Note: isSyncing is already set to true when this is called
        setSyncError(null)

        try {
            await onEdit(credentialId, newName.trim())
            previousNameRef.current = newName.trim()
        } catch (error: any) {
            // On error, revert to previous name
            setLocalName(previousNameRef.current)
            setSyncError(error.message || 'Failed to update device name')

            // Show error briefly, then hide
            setTimeout(() => setSyncError(null), 5000)
        } finally {
            setIsSyncing(false)
        }
    }, [onEdit])

    // Handle name change with debouncing
    const handleNameChange = (newName: string) => {
        // Update local state immediately (optimistic UI)
        setLocalName(newName)

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Show spinner immediately when value is out of sync
        const isOutOfSync = newName.trim() !== previousNameRef.current.trim()

        if (isOutOfSync) {
            setIsSyncing(true)
            // Set new timer for background sync (1 second debounce)
            debounceTimerRef.current = setTimeout(() => {
                debouncedSave(credential.credentialId, newName)
            }, 1000)
        } else {
            // Value is back to original - cancel sync
            setIsSyncing(false)
        }
    }

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    // Get device icon based on type
    const getDeviceIcon = () => {
        const deviceType = credential.metadata?.device_type
        const name = (localName || '').toLowerCase()
        const os = credential.metadata?.operating_system?.toLowerCase() || ''

        // Check metadata device type first
        if (deviceType === 'mobile') return <MdSmartphone className="text-2xl"/>
        if (deviceType === 'tablet') return <MdTablet className="text-2xl"/>
        if (deviceType === 'desktop') return <MdComputer className="text-2xl"/>

        // Check OS for mobile/tablet detection
        if (os.includes('ios') || os.includes('android')) {
            if (os.includes('ipad') || name.includes('ipad') || name.includes('tablet')) {
                return <MdTablet className="text-2xl"/>
            }
            return <MdSmartphone className="text-2xl"/>
        }

        // Check name for device type hints
        if (name.includes('phone') || name.includes('mobile') || name.includes('iphone') || name.includes('android')) {
            return <MdSmartphone className="text-2xl"/>
        }
        if (name.includes('ipad') || name.includes('tablet')) {
            return <MdTablet className="text-2xl"/>
        }
        if (name.includes('macbook') || name.includes('laptop') || name.includes('desktop') || name.includes('imac') || name.includes('pc')) {
            return <MdComputer className="text-2xl"/>
        }

        // Hardware keys
        if (name.includes('yubikey') || name.includes('titan') || name.includes('key')) {
            return <MdKey className="text-2xl"/>
        }

        // Biometric
        if (name.includes('touch') || name.includes('face') || name.includes('fingerprint')) {
            return <MdFingerprint className="text-2xl"/>
        }

        // Default to computer icon for desktop OS
        if (os.includes('mac') || os.includes('windows') || os.includes('linux')) {
            return <MdComputer className="text-2xl"/>
        }

        // Fallback to key icon
        return <MdKey className="text-2xl"/>
    }

    // Get OS icon
    const getOSIcon = () => {
        const os = credential.metadata?.operating_system?.toLowerCase() || ''
        if (os.includes('mac') || os.includes('ios')) return <FaApple className="text-sm opacity-60"/>
        if (os.includes('windows')) return <FaWindows className="text-sm opacity-60"/>
        if (os.includes('android')) return <FaAndroid className="text-sm opacity-60"/>
        return null
    }

    // Get browser icon
    const getBrowserIcon = () => {
        const browser = credential.metadata?.browser?.toLowerCase() || ''
        if (browser.includes('chrome')) return <FaChrome className="text-sm opacity-60"/>
        if (browser.includes('firefox')) return <FaFirefox className="text-sm opacity-60"/>
        if (browser.includes('edge')) return <FaEdge className="text-sm opacity-60"/>
        if (browser.includes('safari')) return <FaSafari className="text-sm opacity-60"/>
        return null
    }

    // Get color based on device type
    const getIconColor = () => {
        if (credential.metadata?.is_synced) return 'text-blue-500'
        const deviceType = credential.metadata?.device_type
        if (deviceType === 'mobile') return 'text-green-500'
        if (deviceType === 'tablet') return 'text-purple-500'
        if (deviceType === 'desktop') return 'text-gray-600'
        return 'text-gray-500'
    }

    const formatDate = (timestamp: number | string) => {
        const date = new Date(timestamp)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return 'Today'
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays} days ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
        return date.toLocaleDateString()
    }

    return (
        <div
            className="group bg-white px-6 pt-2 pb-4 transition-all duration-200">
            <div className="flex items-start justify-between">
                {/* Left side - Icon and Info */}
                <div className="flex space-x-3 flex-1">
                    {/* Device Icon */}
                    <div className={`${getIconColor()} flex-shrink-0 mt-1.5`}>
                        {getDeviceIcon()}
                    </div>

                    {/* Device Info */}
                    <div className="flex-1 min-w-0">
                        {/* Name - Inline Editable */}
                        <div className="flex items-center space-x-2 mb-1">
                            <input
                                type="text"
                                value={localName}
                                onChange={(e) => handleNameChange(e.target.value)}
                                onFocus={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb'
                                    e.currentTarget.style.border = '1px solid #d1d5db'
                                }}
                                onBlur={(e) => {
                                    // Force sync on blur if there's a pending change
                                    if (debounceTimerRef.current) {
                                        clearTimeout(debounceTimerRef.current)
                                        debouncedSave(credential.credentialId, localName)
                                    }
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                    e.currentTarget.style.border = '1px solid transparent'
                                }}
                                className="flex-1 text-base font-medium text-gray-900"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent',
                                    outline: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s, border-color 0.2s'
                                }}
                                placeholder="Device name"
                            />

                            {/* Sync indicator */}
                            {isSyncing && (
                                <div title="Syncing..." className="flex-shrink-0">
                                    <svg
                                        className="animate-spin h-4 w-4 text-blue-500"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            )}

                            {credential.metadata?.is_synced && !isSyncing && (
                                <MdCloud className="text-blue-500 text-sm"
                                         title={`Synced via ${credential.metadata.sync_provider || 'cloud'}`}/>
                            )}
                        </div>

                        {/* Error message */}
                        {syncError && (
                            <div className="text-xs text-red-600 mb-1">
                                {syncError}
                            </div>
                        )}

                        {/* Device Details - All in one line */}
                        <div className="flex items-center space-x-1 text-xs text-gray-500 pl-2">
                            {credential.metadata?.browser && (
                                <>
                                    {getBrowserIcon()}
                                    <span>{credential.metadata.browser}</span>
                                </>
                            )}

                            {credential.metadata?.operating_system && (
                                <>
                                    {credential.metadata?.browser && <span>•</span>}
                                    {getOSIcon()}
                                    <span>{credential.metadata.operating_system}</span>
                                </>
                            )}

                            {/* Added date */}
                            <span>•</span>
                            <span className="text-gray-400">Added {formatDate(credential.createdAt)}</span>

                            {/* Last used */}
                            {credential.metadata?.last_used && (
                                <>
                                    <span>•</span>
                                    <span className="text-gray-400">Used {formatDate(credential.metadata.last_used)}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-1 ml-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onDelete(credential.credentialId, localName || 'Security Key')
                        }}
                        disabled={isDeleting}
                        className="p-2 rounded-lg disabled:opacity-50 flex items-center justify-center cursor-pointer"
                        style={{
                            color: '#9ca3af',
                            backgroundColor: 'transparent',
                            transition: 'color 0.2s, background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isDeleting) {
                                e.currentTarget.style.color = '#7728ff'
                                e.currentTarget.style.backgroundColor = '#E7DBFC'
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isDeleting) {
                                e.currentTarget.style.color = '#9ca3af'
                                e.currentTarget.style.backgroundColor = 'transparent'
                            }
                        }}
                        title="Remove security key"
                    >
                        <MdDelete className="text-lg"/>
                    </button>
                </div>
            </div>

            {/* Sync Provider Badge */}
            {credential.metadata?.sync_provider && (
                <div className="mt-3 pt-3 border-t border-gray-100">
          <span
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
            <MdCloud className="mr-1"/>
              {credential.metadata.sync_provider}
          </span>
                </div>
            )}
        </div>
    )
}
